import { ExternalLink, Info, TrendingDown, TrendingUp } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * AcademicFitPanel — three-row A-Level offer breakdown that never
 * collapses signals into each other.
 *
 *   Advertised offer   — what the uni promises (cited from provider page)
 *   Typical intake     — what enrolled students actually scored (HESA)
 *   Uni-wide typical   — family-level floor across the whole institution
 *
 * Every row shows its source and confidence in a `?` tooltip so
 * families can read the difference between "the offer" and
 * "who got in" — a distinction that matters for contextual admits
 * and highly selective courses.
 *
 * When advertised ≠ intake, we add a chip explaining the direction:
 *   - intake harder → this course is more competitive than the offer
 *     suggests (self-selection + rejections)
 *   - intake softer → contextual admits pull the intake below the
 *     advertised bar
 */

// UCAS 2017 tariff points per letter — used to rank two grade strings
// so we can decide whether the intake is harder or softer than the
// advertised offer.
const _POINT = { "A*": 56, A: 48, B: 40, C: 32, D: 24, E: 16 };

function _tariffPoints(grades) {
  if (!grades) return null;
  // Strip parenthetical subject notes ("(incl A in Maths)") first —
  // otherwise "A*AA (incl A in Maths)" would parse an extra "A".
  const stripped = String(grades).replace(/\([^)]*\)/g, "").trim();
  const tokens = stripped.match(/A\*|[A-E]/g) || [];
  return tokens.reduce((sum, tok) => sum + (_POINT[tok] || 0), 0) || null;
}

function _compareGrades(a, b) {
  const pa = _tariffPoints(a);
  const pb = _tariffPoints(b);
  if (pa == null || pb == null) return 0;
  return pa === pb ? 0 : pa > pb ? 1 : -1;
}

// "2026-08-26T16:06:47.356617+00:00" → " · verified 26 Aug 2026"
// Falls back to "" so the surrounding text reads correctly.
function _fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return ` · verified ${s}`;
}

function Row({ label, value, source, tooltip, evidence, testid, className = "" }) {
  return (
    <div
      className={`grid grid-cols-[minmax(0,140px)_1fr_auto] items-baseline gap-3 py-2.5 border-b border-stone-200 last:border-b-0 ${className}`}
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1">
        {label}
        {tooltip && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Explain ${label}`}
                  className="text-stone-400 hover:text-[#4C1D95] transition"
                >
                  <Info className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs bg-[#002147] text-white text-[11px] leading-relaxed p-3"
              >
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="font-editorial text-lg text-stone-900">
        {value || <span className="text-stone-400 font-mono text-sm">—</span>}
      </div>
      <div className="text-[10px] text-stone-500 text-right leading-tight">
        {evidence?.url ? (
          <a
            href={evidence.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 hover:text-[#4C1D95] hover:underline"
            data-testid={`${testid}-source-link`}
          >
            {source}
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          source
        )}
      </div>
    </div>
  );
}

export default function AcademicFitPanel({ signals }) {
  if (!signals) return null;
  const { advertised, intake, uniWide, ucasTariff, entry_gate } = signals;

  // Portfolio / audition-led — the letter-grade rows aren't the story.
  if (entry_gate === "portfolio" || entry_gate === "audition") {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50/60 p-4"
        data-testid="academic-fit-panel-portfolio"
      >
        <div className="label-uppercase !text-[10px] text-amber-900 mb-1">
          {entry_gate === "audition" ? "Audition-led" : "Portfolio-led"}
        </div>
        <p className="text-sm text-amber-900 leading-relaxed">
          Entry is determined at audition / portfolio review, not by a fixed
          grade offer. Meeting the minimum tariff below is a threshold, not
          the deciding factor.
        </p>
        {ucasTariff?.standard != null && (
          <div className="mt-2 font-mono text-sm text-amber-900">
            Minimum tariff · {ucasTariff.standard} pts
            {ucasTariff.contextual != null && ` (contextual ${ucasTariff.contextual})`}
          </div>
        )}
      </div>
    );
  }

  // UCAS-tariff-only offer (very rare on non-portfolio courses; still
  // valid) — surface as a single line, no letter rows.
  if (!advertised && !intake && ucasTariff?.standard != null) {
    return (
      <div
        className="rounded-md border border-stone-200 bg-white p-4"
        data-testid="academic-fit-panel-tariff-only"
      >
        <Row
          label="UCAS tariff"
          value={
            ucasTariff.contextual != null
              ? `${ucasTariff.standard} pts (contextual ${ucasTariff.contextual})`
              : `${ucasTariff.standard} pts`
          }
          source="Standard offer"
          testid="academic-fit-row-tariff"
          tooltip="The university publishes this course's offer as a UCAS tariff-points bar rather than specific A-Level letters. Convert your own grades on the Grade Calculator to see how you compare."
        />
      </div>
    );
  }

  // Detect the intake-vs-advertised gap so we can annotate it.
  let gapChip = null;
  if (advertised && intake && advertised.value !== intake.value) {
    const cmp = _compareGrades(intake.value, advertised.value);
    if (cmp > 0) {
      gapChip = (
        <div
          data-testid="academic-fit-gap-chip-harder"
          className="mt-3 flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2 text-[11px] text-rose-900"
        >
          <TrendingUp className="h-3.5 w-3.5 shrink-0" />
          <span>
            <b>Intake harder than the advertised offer.</b> Students entering
            this course last cycle scored above the published typical — a
            signal that self-selection and rejections make it more competitive
            in practice than the offer alone suggests.
          </span>
        </div>
      );
    } else if (cmp < 0) {
      gapChip = (
        <div
          data-testid="academic-fit-gap-chip-softer"
          className="mt-3 flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-[11px] text-emerald-900"
        >
          <TrendingDown className="h-3.5 w-3.5 shrink-0" />
          <span>
            <b>Intake softer than the advertised offer.</b> Contextual admits
            and clearing places pull the enrolled cohort below the published
            typical — worth checking your eligibility for contextual review.
          </span>
        </div>
      );
    }
  }

  return (
    <div
      className="rounded-md border border-stone-200 bg-white p-4 mb-3"
      data-testid="academic-fit-panel"
    >
      <div className="font-underground-sc text-xs tracking-wider text-stone-500 mb-2">
        A-Level · offer &amp; intake
      </div>
      <Row
        label="Advertised"
        value={advertised?.value}
        source={
          advertised?.evidence?.url
            ? `Provider${_fmtDate(advertised.evidence.date_checked)}`
            : advertised
              ? "Editorial · unverified"
              : "Not yet verified"
        }
        evidence={advertised?.evidence}
        testid="academic-fit-row-advertised"
        tooltip="The A-Level offer this course publishes — what you'll need to hit to convert an offer into a place. Sourced from the provider's own admissions page. Missing rows mean we haven't yet located a citation gated on the university's minimum; we default to the university-wide typical instead."
      />
      <Row
        label="Typical intake"
        value={intake?.value}
        source={
          intake
            ? `HESA KIS${intake.sample_size ? ` · ${intake.sample_size} students` : ""}`
            : "No HESA record"
        }
        testid="academic-fit-row-intake"
        tooltip="The median A-Level grade of students who actually enrolled on this course last cycle, from HESA's Key Information Set. Different from the advertised offer: contextual admits soften it, self-selection and rejections harden it. Use as a reality check on the promised bar."
      />
      <Row
        label="Uni-wide"
        value={uniWide?.value}
        source={
          uniWide?.minimum
            ? `Family floor · min ${uniWide.minimum}`
            : "Family typical"
        }
        testid="academic-fit-row-uni-wide"
        tooltip="The university-wide typical (and minimum) A-Level offer that applies across all courses at this institution — a family-level floor. If a course is missing an advertised offer, this is the coarsest safe fallback."
      />
      {gapChip}
    </div>
  );
}
